import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, IconButton, Typography
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell,
  ComposedChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import {
  fetchScoreData,
  fetchScoreConversations,
} from "../../../services/insightModule/insightService.js";
import DatePickerComponent from "../../../common/components/DatePickerComponent.jsx";
import ConversationDrillDownDialog from "../../../components/ConversationDrillDownDialog";
import { STRINGS } from "../../../common/constants/commonConstants.js";
import { formatDateTime } from "../../../common/constants/insightModule/scoreConstants.js";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const AMBER  = "#BA7517";
const GREEN  = "#1D9E75";
const PINK   = "#D4537E";
const RED    = "#A32D2D";
const TEAL   = "#85B7EB";

const SCORE_KEYS = ["one", "two", "three", "four", "five"];

const SCORE_COLORS = {
  five:  GREEN,
  four:  TEAL,
  three: AMBER,
  two:   PINK,
  one:   RED,
};

const SCORE_LABELS = {
  five:  "Score 5",
  four:  "Score 4",
  three: "Score 3",
  two:   "Score 2",
  one:   "Score 1",
};

// ── Helpers ────────────────────────────────────────────────────────
function parseLocalDate(val) {
  if (!val) return null;
  const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
}

function fmtDate(val) {
  const d = parseLocalDate(val);
  if (!d || isNaN(d)) return val || "";
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

function safeNum(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

// ── Sub-components ─────────────────────────────────────────────────
function KpiCard({ label, value, color = BLUE, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        background: "#f7f7f7", borderRadius: 2,
        p: "10px 14px", minWidth: 0, flex: 1,
        cursor: onClick ? "pointer" : "default",
        transition: "background .15s",
        "&:hover": onClick ? { background: "#efefef" } : {},
      }}
    >
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function ChartCard({ title, children, height = 200 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

// ── Main Component ─────────────────────────────────────────────────
const Score = () => {
  const [startDate, setStartDate] = useState(
    () => new Date(new Date().getFullYear(), new Date().getMonth(), 1)
  );
  const [endDate,   setEndDate]   = useState(() => new Date());
  const [channel,   setChannel]   = useState("Texts");
  const [data,      setData]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [page,      setPage]      = useState(0);
  const [drill,     setDrill]     = useState(null);
  const rowsPerPage = 25;

  const load = useCallback(async (start, end, ch) => {
    setLoading(true);
    setError("");
    setPage(0);
    try {
      const result = await fetchScoreData({
        startDate: formatDateTime(start),
        endDate:   formatDateTime(end),
        scoreType: ch,
      });
      setData(result || []);
    } catch (e) {
      console.error(e);
      setError("Failed to load score data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(startDate, endDate, "Texts");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── KPIs ───────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const total    = data.reduce((a,b) => a + safeNum(b.one) + safeNum(b.two) +
                      safeNum(b.three) + safeNum(b.four) + safeNum(b.five), 0);
    const sumScore = data.reduce((a,b) =>
      a + safeNum(b.one)*1 + safeNum(b.two)*2 +
          safeNum(b.three)*3 + safeNum(b.four)*4 + safeNum(b.five)*5, 0);
    const counts = {
      one:   data.reduce((a,b) => a + safeNum(b.one),   0),
      two:   data.reduce((a,b) => a + safeNum(b.two),   0),
      three: data.reduce((a,b) => a + safeNum(b.three), 0),
      four:  data.reduce((a,b) => a + safeNum(b.four),  0),
      five:  data.reduce((a,b) => a + safeNum(b.five),  0),
    };
    return {
      total,
      avgScore: total > 0 ? sumScore / total : 0,
      ...counts,
      onePct:   total > 0 ? counts.one   / total * 100 : 0,
      twoPct:   total > 0 ? counts.two   / total * 100 : 0,
      threePct: total > 0 ? counts.three / total * 100 : 0,
      fourPct:  total > 0 ? counts.four  / total * 100 : 0,
      fivePct:  total > 0 ? counts.five  / total * 100 : 0,
    };
  }, [data]);

  // ── Chart data ─────────────────────────────────────────────────
  const chartData = useMemo(() =>
    [...data]
      .sort((a, b) => {
        const da = parseLocalDate(a.ConvDate);
        const db = parseLocalDate(b.ConvDate);
        return (da?.getTime() || 0) - (db?.getTime() || 0);
      })
      .map(r => ({
        date:      fmtDate(r.ConvDate),
        _convDate: r.ConvDate,
        one:       safeNum(r.one),
        two:       safeNum(r.two),
        three:     safeNum(r.three),
        four:      safeNum(r.four),
        five:      safeNum(r.five),
        avg:       safeNum(r.avg_score),
      }))
  , [data]);

  // ── Distribution bar data ──────────────────────────────────────
  const distData = useMemo(() => [
    { label: "Score 1", key: "one",   count: kpis.one,   pct: kpis.onePct,   color: RED   },
    { label: "Score 2", key: "two",   count: kpis.two,   pct: kpis.twoPct,   color: PINK  },
    { label: "Score 3", key: "three", count: kpis.three, pct: kpis.threePct, color: AMBER },
    { label: "Score 4", key: "four",  count: kpis.four,  pct: kpis.fourPct,  color: TEAL  },
    { label: "Score 5", key: "five",  count: kpis.five,  pct: kpis.fivePct,  color: GREEN },
  ], [kpis]);

  // ── Pagination ─────────────────────────────────────────────────
  const sortedData  = useMemo(() =>
    [...data].sort((a, b) => {
      const da = parseLocalDate(a.ConvDate);
      const db = parseLocalDate(b.ConvDate);
      return (db?.getTime() || 0) - (da?.getTime() || 0);
    })
  , [data]);

  const pagedData   = useMemo(() =>
    sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  , [sortedData, page]);

  const totalPages  = Math.ceil(sortedData.length / rowsPerPage);

  // ── Drill-down ─────────────────────────────────────────────────
  const openDrill = useCallback(({ score = null, date = null }) => {
    const scoreColor = score ? SCORE_COLORS[score] : null;
    const scoreLabel = score ? SCORE_LABELS[score] : null;

    const title =
      score && date ? "Conversations by score & date" :
      score         ? `Conversations with ${scoreLabel}` :
                      `Conversations on ${fmtDate(date)}`;

    const dStart = date ? new Date(date) : startDate;
    const dEnd   = date ? new Date(date) : endDate;

    setDrill({
      title,
      metric: score ? { label: "Score", value: scoreLabel, color: scoreColor } : null,
      context: [
        date && { label: "Date", value: fmtDate(date) },
        !date && startDate && { label: "From", value: fmtDate(startDate) },
        !date && endDate   && { label: "To",   value: fmtDate(endDate) },
      ].filter(Boolean),
      channel,
      // The DB stores scores as bare digits ("1"–"5"), so the chip on each
      // card will display the digit. Map digits + word + label forms so the
      // chip color works regardless of which form the description holds.
      emotionColors: {
        ...Object.fromEntries(SCORE_KEYS.map(k => [k, SCORE_COLORS[k]])),
        ...Object.fromEntries(SCORE_KEYS.map(k => [SCORE_LABELS[k], SCORE_COLORS[k]])),
        "1": SCORE_COLORS.one,
        "2": SCORE_COLORS.two,
        "3": SCORE_COLORS.three,
        "4": SCORE_COLORS.four,
        "5": SCORE_COLORS.five,
      },
      fetchData: () => fetchScoreConversations({
        score,
        startDate:   dStart ? dStart.toISOString() : null,
        endDate:     dEnd   ? dEnd.toISOString()   : null,
        channeltype: channel,
      }),
    });
  }, [startDate, endDate, channel]);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };

  // 40px drill icon column added at the start
  const COLS    = "40px 100px 90px 90px 90px 90px 90px 80px 90px";
  const HEADERS = ["", "Date", "Score 1", "Score 2", "Score 3", "Score 4", "Score 5", "Total", "Avg Score"];

  const CELL_COLS = [
    { key: "one",   color: RED   },
    { key: "two",   color: PINK  },
    { key: "three", color: AMBER },
    { key: "four",  color: TEAL  },
    { key: "five",  color: GREEN },
  ];

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Filters ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <DatePickerComponent
          label={STRINGS.START_DATE}
          date={startDate}
          onDateChange={setStartDate}
          marginRight={0}
        />
        <DatePickerComponent
          label={STRINGS.END_DATE}
          date={endDate}
          onDateChange={d => { if (d >= startDate) setEndDate(d); }}
          marginRight={0}
        />
        <Box
          onClick={() => load(startDate, endDate, channel)}
          sx={{
            px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
            cursor: "pointer", userSelect: "none",
            background: BLUE, color: "#fff", fontWeight: 500,
            alignSelf: "flex-end", mb: "1px",
            "&:hover": { background: "#2a6db5" },
          }}
        >
          Search
        </Box>

        {/* Channel toggle */}
        <Box sx={{
          display: "flex", ml: "auto",
          border: "0.5px solid", borderColor: "divider",
          borderRadius: "20px", overflow: "hidden",
          alignSelf: "flex-end",
        }}>
          {["Texts", "Emails"].map(ch => (
            <Box key={ch} onClick={() => {
              setChannel(ch);
              load(startDate, endDate, ch);
            }} sx={{
              px: "14px", py: "5px", fontSize: 13,
              fontWeight:  channel === ch ? 500 : 400,
              cursor: "pointer", userSelect: "none",
              background:  channel === ch ? BLUE : "transparent",
              color:       channel === ch ? "#fff" : "text.secondary",
              transition:  "all .15s",
            }}>
              {ch}
            </Box>
          ))}
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
          <CircularProgress size={20} /> Loading…
        </Box>
      )}

      {!loading && data.length === 0 && !error && (
        <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
          No data for selected range.
        </Typography>
      )}

      {!loading && data.length > 0 && (
        <>
          {/* ── SUMMARY KPIs ──────────────────────────────────── */}
          <SectionTitle>Summary</SectionTitle>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
            <KpiCard label="Total records" value={kpis.total.toLocaleString()}    color={BLUE}  />
            <KpiCard label="Avg score"     value={kpis.avgScore.toFixed(2)}       color={AMBER} />
            <KpiCard label="Score 5"       value={`${kpis.fivePct.toFixed(1)}%`}  color={GREEN}
              onClick={() => openDrill({ score: "five" })} />
            <KpiCard label="Score 4"       value={`${kpis.fourPct.toFixed(1)}%`}  color={TEAL}
              onClick={() => openDrill({ score: "four" })} />
            <KpiCard label="Score 3"       value={`${kpis.threePct.toFixed(1)}%`} color={AMBER}
              onClick={() => openDrill({ score: "three" })} />
            {/* Score 1–2 is a combined bucket; not drillable as one filter */}
            <KpiCard label="Score 1–2"
              value={`${(kpis.onePct + kpis.twoPct).toFixed(1)}%`}                color={RED}   />
          </Box>

          {/* ── STACKED AREA CHART (click point → drill date) ── */}
          <SectionTitle>Score distribution over time</SectionTitle>

          <ChartCard height={280}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                style={{ cursor: "pointer" }}
                onClick={(state) => {
                  const dt = state?.activePayload?.[0]?.payload?._convDate;
                  if (dt) openDrill({ date: dt });
                }}
              >
                <defs>
                  {Object.entries(SCORE_COLORS).map(([key, color]) => (
                    <linearGradient key={key} id={`grad_${key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={color} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={color} stopOpacity={0.4} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis tick={axisStyle} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                {SCORE_KEYS.map(key => (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    name={SCORE_LABELS[key]}
                    stackId="score"
                    stroke={SCORE_COLORS[key]}
                    fill={`url(#grad_${key})`}
                    strokeWidth={1}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── AVG SCORE TREND (click point → drill date) ───── */}
          <SectionTitle>Avg score trend</SectionTitle>

          <ChartCard height={200}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                style={{ cursor: "pointer" }}
                onClick={(state) => {
                  const dt = state?.activePayload?.[0]?.payload?._convDate;
                  if (dt) openDrill({ date: dt });
                }}
              >
                <defs>
                  <linearGradient id="avgGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={AMBER} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={AMBER} stopOpacity={0}    />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis tick={axisStyle} domain={[0, 5]} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={v => [Number(v).toFixed(2), "Avg score"]}
                />
                <Area type="monotone" dataKey="avg" name="Avg score"
                  stroke={AMBER} fill="url(#avgGrad)"
                  strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── DISTRIBUTION BAR (click bar → drill score) ───── */}
          <SectionTitle>Overall score distribution</SectionTitle>

          <ChartCard height={180}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={distData} layout="vertical"
                margin={{ top: 4, right: 60, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" horizontal={false} />
                <XAxis type="number" tick={axisStyle} domain={[0, 100]}
                  tickFormatter={v => `${v}%`} />
                <YAxis type="category" dataKey="label"
                  tick={{ fontSize: 11, fill: "#555" }} width={60} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n, p) => [
                    `${p.payload.pct.toFixed(2)}% (${p.payload.count.toLocaleString()})`,
                    "Count"
                  ]}
                />
                <Bar
                  dataKey="pct"
                  name="%"
                  radius={[0,4,4,0]}
                  cursor="pointer"
                >
                  {distData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={entry.color}
                      onClick={() => openDrill({ score: entry.key })}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* ── DATA TABLE ────────────────────────────────────── */}
          <SectionTitle>Detail</SectionTitle>

          <Box sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, overflow: "hidden",
          }}>
            {/* Header */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: COLS,
              background: "#f7f7f7",
              borderBottom: "0.5px solid", borderColor: "divider",
              px: 2, py: 1,
              fontSize: 11, fontWeight: 500,
              color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.05em",
              alignItems: "center",
            }}>
              {HEADERS.map((h, i) => (
                <Box key={i} sx={{ textAlign: i <= 1 ? "left" : "right" }}>{h}</Box>
              ))}
            </Box>

            {/* Rows */}
            {pagedData.map((row, i) => {
              const total = SCORE_KEYS.reduce((a,k) => a + safeNum(row[k]), 0) || 1;
              const avg   = safeNum(row.avg_score) ||
                (SCORE_KEYS.reduce((a,k,idx) => a + safeNum(row[k]) * (idx+1), 0) / total);
              return (
                <Box key={i} sx={{
                  display: "grid",
                  gridTemplateColumns: COLS,
                  px: 2, py: "6px",
                  fontSize: 12,
                  borderBottom: "0.5px solid", borderColor: "divider",
                  background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                  "&:hover": { background: "rgba(55,138,221,0.06)" },
                  alignItems: "center",
                }}>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => openDrill({ date: row.ConvDate })}
                      sx={{ width: 24, height: 24 }}
                      title="View conversations for this date"
                    >
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                  <Box>{fmtDate(row.ConvDate)}</Box>
                  {CELL_COLS.map(({ key, color }) => {
                    const count = safeNum(row[key]);
                    const clickable = count > 0;
                    return (
                      <Box
                        key={key}
                        onClick={clickable
                          ? () => openDrill({ score: key, date: row.ConvDate })
                          : undefined}
                        sx={{
                          textAlign: "right",
                          color: clickable ? color : "text.disabled",
                          fontWeight: clickable && key === "five" ? 500 : 400,
                          cursor: clickable ? "pointer" : "default",
                          "&:hover": clickable ? { textDecoration: "underline" } : {},
                        }}
                      >
                        {count}
                        <Box component="span" sx={{ fontSize: 10, color: "text.secondary", ml: 0.5 }}>
                          ({(count/total*100).toFixed(0)}%)
                        </Box>
                      </Box>
                    );
                  })}
                  <Box sx={{ textAlign: "right", fontWeight: 500 }}>
                    {total.toLocaleString()}
                  </Box>
                  <Box sx={{ textAlign: "right", color: AMBER, fontWeight: 500 }}>
                    {avg.toFixed(2)}
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{
              display: "flex", justifyContent: "flex-end",
              alignItems: "center", gap: 1, mt: 1, fontSize: 12,
              color: "text.secondary",
            }}>
              <Box
                onClick={() => setPage(p => Math.max(0, p - 1))}
                sx={{
                  px: "10px", py: "3px", borderRadius: "20px",
                  border: "0.5px solid", borderColor: "divider",
                  cursor: page === 0 ? "default" : "pointer",
                  opacity: page === 0 ? 0.4 : 1,
                  "&:hover": { borderColor: page === 0 ? "divider" : BLUE },
                }}
              >
                ‹
              </Box>
              <span>Page {page + 1} of {totalPages}</span>
              <Box
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                sx={{
                  px: "10px", py: "3px", borderRadius: "20px",
                  border: "0.5px solid", borderColor: "divider",
                  cursor: page >= totalPages - 1 ? "default" : "pointer",
                  opacity: page >= totalPages - 1 ? 0.4 : 1,
                  "&:hover": { borderColor: page >= totalPages - 1 ? "divider" : BLUE },
                }}
              >
                ›
              </Box>
            </Box>
          )}
        </>
      )}

      {/* ── Drill-down popup ───────────────────────────────────── */}
      {drill && (
        <ConversationDrillDownDialog
          open={!!drill}
          onClose={() => setDrill(null)}
          {...drill}
        />
      )}
    </Box>
  );
};

export default Score;